// noinspection DuplicatedCode

import SaveIcon from "@mui/icons-material/Save";
import { keyframes, styled } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import type { MRT_PaginationState as PaginationState } from "material-react-table";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import AHBoolean from "_shared/booleans/boolean";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import MarketplaceTeam from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { Masked } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  fieldHasPendingChange,
  pendingChangeContainerStyles,
} from "work/proposals/comments/_view/pending-change-field-highlight";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import TeamTemplateQuery, {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import {
  useCreateTeamTemplateMutation,
  useGetTeamTemplatesQuery,
} from "work/team-templates/_api/team-template-api";
import {
  getIsLoadingTeamTemplatesByQuery,
  getTeamTemplatesByQuery,
} from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplate from "work/team-templates/team-template";
import EditVendorTeam from "work/work-agreements/teams/vendor-team/_views/vendor-team-edit-view";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";
import {
  getPromoteTeamMemberToLeaderPrompt,
  getTeamLeaderCannotAlsoBeMemberMessage,
  getTeamMemberBecameLeaderMessage,
} from "work/proposals/_views/_shared/team-leader-exclusivity-messages";
import { TemplateOwnershipScope } from "_shared/_views/templates/template-ownership-filter";
import TemplateSelector, { type TemplateSelectorRow } from "_shared/_views/templates/template-selector";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import {
  NO_TEMPLATE_CONTRIBUTIONS,
  TemplateContributions,
  withTemplateDeselected,
  withTemplateSelected,
} from "work/proposals/_views/_shared/template-contributions";
import { EmbeddedTemplatePanelContent } from "work/fee-schedule-templates/_views/_shared/embedded-containers";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
  workPaneNarrowerThan,
  workPaneProseInset,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(({ theme }) => ({
  height: "100%",
  minHeight: "20rem",
  minWidth: 0,
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    minHeight: 0,
  },
}));
// The shared container carries a fixed pixel width for the tabs that are not resizable; inside the
// drag-resizable panel the content has to follow the column instead.
// The height goes with the width. `EmbeddedTemplatePanelContent` is a plain block sized by its
// contents, and the panel below it ends in a paginator MRT drops to the foot of its paper with a
// `margin-top: auto` — which moves nothing unless the paper has height to spare. With this wrapper
// between the column and the panel the paginator sat under the last template rather than at the
// bottom of the column, where the Fee Schedule tab's is; that tab has no wrapper here.
const TemplateContainer = styled(EmbeddedTemplatePanelContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  width: "100%",
  // The fill is for the column case only. On a compact pane this panel is a row in a stack rather
  // than a column beside the roster, and the stack's container has a definite height of its own — so
  // `height: 100%` handed the collapsed accordion the whole tab and left the roster under it with
  // nowhere to draw. See `CollapsibleTemplatePanel`'s `Panel`, which is harmless there for the
  // opposite reason: its parent has no definite height, so the percentage resolves to `auto`.
  [paneIsCompact(theme)]: {
    height: "auto",
  },
}));
// A person revealed under a template: what they are to it, then what they are called. The list this
// panel replaced said so with "Leader" and "Members" subheaders, which a table of named rows has
// nowhere to put — so the role travels with the person instead of heading a group of them.
const PersonNameCell = styled("div")(({ theme }) => ({
  alignItems: "baseline",
  columnGap: theme.spacing(1),
  display: "flex",
  minWidth: 0,
  width: "100%",
}));
const PersonRole = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
  fontSize: "0.7rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
}));
const PersonName = styled("span")(() => ({
  fontSize: "1em",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
/**
 * The roster meets the templates panel beside it, with nothing between them but the resize handle.
 *
 * It used to carry a 24px left margin. The handle is already the boundary between the two panels, so
 * a gutter as well read as the roster being indented inside its own column rather than as the two
 * being held apart — and it was the page's own edges, where there is nothing to divide the content
 * from, that wanted the room instead. The compact arrangement stacks these two and so never had a
 * gutter here to lose.
 */
const TeamSelect = styled(EditVendorTeam)(({ theme }) => ({
  // The roster's own heading strip is prose and takes the inset; the table under it stays flush to
  // the resize divider. `data-roster-header` is the hook `TeamRosterSection` documents for exactly
  // this — addressing the strip without reaching into an Emotion class name.
  "& [data-roster-header]": workPaneProseInset(theme),
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  [paneIsCompact(theme)]: {
    marginTop: theme.spacing(1),
    width: "100%",
  },
}));
const TeamSelectContainer = styled(Grid)(({ theme }) => ({
  minWidth: 0,
  [paneIsCompact(theme)]: {
    width: "100%",
  },
}));
const IssueContainer = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    [paneIsCompact(theme)]: {
      "& .MuiTypography-root": {
        fontSize: "0.9rem",
      },
    },
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    padding: active ? theme.spacing(1) : "initial",
    borderRadius: theme.shape.borderRadius,
  }),
);
/** The tab's issue notice and its restricted switch — prose, so inset. `workPaneProseInset`. */
const TopControls = styled("div")(({ theme }) => ({
  ...workPaneProseInset(theme),
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const PendingChangeControlFrame = styled("span", {
  shouldForwardProp: (prop) => prop !== "pendingChange",
})<{ pendingChange?: boolean }>(({ theme, pendingChange }) => ({
  display: "inline-flex",
  ...(pendingChange && pendingChangeContainerStyles(theme)),
}));
// Sized like the "From Marketplace Team" button it now shares a row with.
const CreateTemplateAction = styled(LoadingButton)(() => ({
  width: "fit-content",
}));

type TeamTabProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  disableEditing?: boolean;
  /**
   * Whether this actor may change the Vendor Team's identity — its leader and its marketplace team —
   * as distinct from its roster. Editing the proposal and configuring its initial setup are separate
   * permissions: a reviewer holding `canEditDraft` may add and remove members, while the leader and
   * marketplace identity belong to the creator until first submission
   * (`canConfigureInitialProposalSetup`). Defaults to true.
   */
  canChangeTeamIdentity?: boolean;
  fieldLockTooltip?: string;
  isDrawerOpen: boolean;
  showPendingChangeHighlights?: boolean;
  onProposalBuilderUpdated: (newProposalBuilder: ProposalBuilder) => void;
  onSelectionDrawerOpenChange?: (isOpen: boolean) => void;
  negotiationId?: string;
  viewerScope?: string;
};

export default function TeamTab(props: Readonly<TeamTabProps>) {
  const INTERFACE_NAME = "DraftProposalTeamTab";
  const {
    activeReviewField,
    proposalBuilder,
    disableEditing,
    fieldLockTooltip,
    isDrawerOpen,
    showPendingChangeHighlights,
    onProposalBuilderUpdated,
    onSelectionDrawerOpenChange,
    negotiationId,
    viewerScope,
  } = props;
  // Read off `props` rather than the destructuring above so the default reads as a default rather
  // than as a destructuring pattern the React Compiler cannot lower (see app/CLAUDE.md).
  const canChangeTeamIdentity = props.canChangeTeamIdentity !== false;

  const session = useSession();
  const userId = session.user!.id;
  const [templateOwnershipScope, setTemplateOwnershipScope] = useState<TemplateOwnershipScope>("all");
  // The panel's own search box. The team-template API takes no name filter, so this narrows the page
  // in hand rather than the query — see `visibleTeamTemplates`.
  const [templateSearch, setTemplateSearch] = useState("");
  const [templatePagination, setTemplatePagination] = useState<PaginationState>({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });
  const templateQuery = useMemo(() => {
    const filterParameters = new TeamTemplateFilterParameters({
      creatorUserId: templateOwnershipScope === "own" ? userId : undefined,
    });
    const orderParameters = new TeamTemplateOrderParameters({
      orderBy: TeamTemplateOrderField.Name,
      orderDir: OrderDirection.Descending,
    });
    return new TeamTemplateQuery({
      filterParameters,
      orderParameters,
      // The paginator the panel draws has to move the query, or pressing page two would redraw the
      // page the panel already had and call it the second one.
      paginationParameters: PaginationParameters.create(templatePagination),
    });
  }, [templateOwnershipScope, templatePagination, userId]);

  const teamTemplatesApiQuery = useGetTeamTemplatesQuery({ query: templateQuery, interfaceName: INTERFACE_NAME });
  const [createTeamTemplate] = useCreateTeamTemplateMutation();
  const teamTemplates = useSelector(getTeamTemplatesByQuery(templateQuery));
  const isLoadingTeamTemplates =
    useSelector(getIsLoadingTeamTemplatesByQuery(templateQuery)) ||
    teamTemplatesApiQuery.isLoading ||
    teamTemplatesApiQuery.isFetching;
  const proposal = useMemo(() => proposalBuilder.buildNewDraft(), [proposalBuilder]);
  const individualUserIds: Set<string> = useMemo(() => {
    const userIds: Set<string> = new Set();
    if (teamTemplates === undefined) return userIds;
    if (proposal) proposal.associatedUserIds.forEach((id) => userIds.add(id.value));
    for (const templateTeam of teamTemplates?.data ?? []) {
      userIds.add(templateTeam.leader.userId.value);
      for (const member of templateTeam.members) {
        userIds.add(member.userId.value);
      }
    }
    return userIds;
  }, [proposal, teamTemplates]);

  const individuals = useSelector(getIndividualsByUserIds(individualUserIds));
  const missingIndividualUserIds = useMemo(() => {
    const loadedUserIds = new Set(individuals.map((ind) => ind.userId.value));
    const missingIds = new Set<string>();
    individualUserIds.forEach((userId) => {
      if (!loadedUserIds.has(userId)) {
        missingIds.add(userId);
      }
    });
    return missingIds;
  }, [individualUserIds, individuals]);
  const individualQuery = useGetIndividualsByUserIdsQuery(
    missingIndividualUserIds.size ? { userIds: missingIndividualUserIds } : skipToken,
  );

  const [templateContributions, setTemplateContributions] =
    useState<TemplateContributions>(NO_TEMPLATE_CONTRIBUTIONS);
  // A template only ever fills an EMPTY leader slot, so deselecting only ever empties it again. When
  // the user answered the "Replace leader?" prompt with Replace, the leader they chose to swap in is
  // theirs to keep — the template did not put a leader where there was none.
  //
  // The leader a template supplied is remembered, not merely the fact that it supplied one: whoever
  // is leading the team may since have been replaced, by hand or by a later template's "Replace
  // leader", and deselecting the original template must not carry off a leader it did not put there.
  const [leaderKeySuppliedByTemplateId, setLeaderKeySuppliedByTemplateId] =
    useState<Readonly<Record<string, string>>>({});
  const [isCreateTemplateDialogOpen, setIsCreateTemplateDialogOpen] = useState(false);
  const [createTemplateName, setCreateTemplateName] = useState("");
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const vendorTeam = proposalBuilder.currentSpec.workAgreement?.vendorTeam;
  const selectedVendorTeam = Masked.isMasked(vendorTeam) ? undefined : vendorTeam;
  const selectedLeader = selectedVendorTeam?.leader ?? null;
  const selectedMembers = selectedVendorTeam?.members ?? [];
  const selectedTemplateIds = session.context?.viewingAsVendor
    ? proposalBuilder.currentSpec.workAgreement?.vendorTeamTemplateIds
    : proposalBuilder.currentSpec.workAgreement?.clientTeamTemplateIds;
  const selectedTemplateCount = selectedTemplateIds?.length ?? 0;
  const pendingChangeFields = showPendingChangeHighlights
    ? proposalBuilder.sessionHistory?.flatMap((change) => change.field ? [change.field] : []) ?? []
    : [];
  const showLockIcon = Boolean(disableEditing && fieldLockTooltip);
  const lockIcon = showLockIcon ? <ReviewerEditLockIcon title={fieldLockTooltip} /> : undefined;

  const issues = useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [
          ProposalField.Team,
          ProposalField.TeamRestriction,
          ProposalField.TeamLeader(issue.field.id),
          ProposalField.TeamMember(issue.field.id ?? Guid.EMPTY),
        ].some((field) => field.isEqualTo(issue.field)),
      ),
    [props.issues?.entries],
  );

  const confirm = useConfirmDialog();
  const isMdDownDisplay = useWorkPaneIsCompact();
  const canCreateTemplate = Boolean(
    selectedLeader?.userId &&
    selectedLeader.legalEntityId &&
    session.user?.legalEntityId &&
    !disableEditing,
  );
  // The panel searches the templates IN HAND rather than the query: the team-template API takes an
  // owner and a page and no name, so there is nothing to send. Each name is matched against the
  // typed text case-insensitively, and the panel is handed the survivors as its rows.
  const templateSearchText = templateSearch.trim().toLocaleLowerCase();
  const visibleTeamTemplates = (teamTemplates?.data ?? []).filter(
    (template) => !templateSearchText || template.name.value.toLocaleLowerCase().includes(templateSearchText),
  );

  const setDefaultProposalTeamLeader = useCallback(() => {
    const currentTeam = proposalBuilder.currentSpec.workAgreement?.vendorTeam;
    if (Masked.isMasked(currentTeam)) return;
    // Seating yourself as leader is a team-identity change. An actor who may not make one must not
    // have it made on their behalf: the operation is dropped before it reaches the wire, so the
    // editor would show a leader the saved proposal never gains (#4854).
    if (!canChangeTeamIdentity) return;
    if (
      !currentTeam?.leader &&
      session.accountType === AccountType.Vendor &&
      session.user?.id &&
      session.user.legalEntityId &&
      session.user.individualId
    ) {
      // Fills only the empty leader slot — a leaderless team can still be carrying members and a
      // marketplaceId (e.g. a template's "Replace leader?" prompt vacated the leader a marketplace
      // team arrived with), and this must not build over them (#4515).
      const defaultTeam = (currentTeam ?? new VendorTeam({ members: [] })).clone({
        leader: new EntityMember({
          legalEntityId: session.user.legalEntityId,
          userId: session.user.id,
        }),
      });
      onProposalBuilderUpdated(proposalBuilder.setVendorTeam(defaultTeam));
    }
  }, [proposalBuilder, session, canChangeTeamIdentity, onProposalBuilderUpdated]);

  useEffect(() => {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.vendorTeam)) return;
    setDefaultProposalTeamLeader();
  }, [proposalBuilder, setDefaultProposalTeamLeader]);

  async function updateTeamOnProposalBuilder(leader?: EntityMember, members?: EntityMember[]) {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.vendorTeam))
      throw new Error("Cannot modify team when it is masked");
    // the collision settles it first and says so; this only guarantees that nothing slips past into a
    // team the API would reject.
    // Cloning the existing team (rather than constructing from just `leader`/`members`) carries every
    // other field — `marketplaceId` included — through an ordinary leader or member edit (#4515).
    const team = (selectedVendorTeam ?? new VendorTeam({ members: [] })).clone({
      leader,
      members: members ?? [],
    }).withLeaderRemovedFromMembers();
    onProposalBuilderUpdated(proposalBuilder.setVendorTeam(team));
  }

  async function handleTemplateSelectionToggled(template: TeamTemplate): Promise<void> {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.vendorTeam)) {
      throw new Error("Cannot modify team when it is masked");
    }
    try {
      if (!template.id) {
        throw new Error("No template selected");
      }
      let updatedSelectedTemplateIds = session.context?.viewingAsVendor
        ? [...(proposalBuilder.currentSpec.workAgreement?.vendorTeamTemplateIds ?? [])]
        : [...(proposalBuilder.currentSpec.workAgreement?.clientTeamTemplateIds ?? [])];

      const isAlreadySelected = updatedSelectedTemplateIds?.some((id) => id.isEqualTo(template.id));

      let updatedTeam = proposalBuilder.currentSpec.workAgreement?.vendorTeam;
      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
        const { contributions, itemKeysToRemove: memberKeysToRemove } = withTemplateDeselected({
          contributions: templateContributions,
          templateId: template.id,
          alsoSuppliedItemKeys: getTeamMemberKeysOfLoadedTemplates(updatedSelectedTemplateIds),
        });
        // Take the leader back only if the one currently seated is still the one this template
        // supplied — a leader the user has since replaced belongs to them.
        // An actor who may not change the team's identity never took a leader from this template in
        // the first place, so deselecting it has none to give back.
        const templateSuppliedCurrentLeader = Boolean(
          canChangeTeamIdentity &&
          selectedLeader &&
          leaderKeySuppliedByTemplateId[template.id.value] === getTeamMemberKey(selectedLeader),
        );
        // Only rewrite a team that exists; an unselected template on a proposal with no team yet has
        // nothing to take back.
        if (selectedVendorTeam) {
          const retainedLeader = templateSuppliedCurrentLeader ? undefined : (selectedLeader ?? undefined);
          // Cloning the existing team carries `marketplaceId` (and any other field) forward
          // automatically — the deselect only ever means to change leader/members (#4515).
          updatedTeam = selectedVendorTeam.clone({
            leader: retainedLeader,
            members: selectedMembers.filter((member) => !memberKeysToRemove.has(getTeamMemberKey(member))),
          }).withLeaderRemovedFromMembers();
        }
        setTemplateContributions(contributions);
        setLeaderKeySuppliedByTemplateId(withoutLeaderSupplier(leaderKeySuppliedByTemplateId, template.id));
      } else {
        updatedSelectedTemplateIds.push(template.id);
        const leaderSlotWasEmpty = !selectedLeader;
        const newLeader = await getNewLeaderFromTemplate(template);
        const members = getNewMembersFromTemplate(template);
        // Whoever is leading the team cannot also sit in the member list (#4491). The merge above is
        // blind to that, so the collision is settled here — before the team is built — and reported,
        // because a member the user watched the template supply must not just quietly fail to appear.
        const mergedTeam = new VendorTeam({ leader: newLeader, members });
        if (newLeader && mergedTeam.membersDisplacedByLeader.length) {
          const wasMemberBeforeToggle = selectedMembers.some((member) => newLeader.isSamePersonAs(member));
          const justBecameLeader = !selectedLeader?.isSamePersonAs(newLeader);
          enqueueSnackbar(
            wasMemberBeforeToggle && justBecameLeader
              ? getTeamMemberBecameLeaderMessage(getIndividualFullName(newLeader))
              : getTeamLeaderCannotAlsoBeMemberMessage(getIndividualFullName(newLeader)),
            { variant: "info" },
          );
        }
        const memberKeysAlreadySelected = new Set(selectedMembers.map(getTeamMemberKey));
        // Undefined when this template supplied no leader — either it never reached the slot, or
        // this actor may not change the team's identity — so it matches no member key below.
        const newLeaderKey = newLeader && getTeamMemberKey(newLeader);
        const templateMemberKeys = template.members.map(getTeamMemberKey);
        setTemplateContributions(
          withTemplateSelected({
            contributions: templateContributions,
            templateId: template.id,
            suppliedItemKeys: templateMemberKeys,
            // A template member who ends up leading was never put on the member list, so deselecting
            // this template has nothing of theirs to take back.
            addedItemKeys: templateMemberKeys.filter(
              (memberKey) => !memberKeysAlreadySelected.has(memberKey) && memberKey !== newLeaderKey,
            ),
          }),
        );
        if (leaderSlotWasEmpty && newLeaderKey) {
          setLeaderKeySuppliedByTemplateId({
            ...leaderKeySuppliedByTemplateId,
            [template.id.value]: newLeaderKey,
          });
        }
        // Selecting a template must not cost a marketplace-sourced team its `marketplaceId` (#4515);
        // cloning the existing team (when there is one) carries it, and every other field, forward.
        updatedTeam = (selectedVendorTeam ?? new VendorTeam({ members: [] }))
          .clone({ leader: newLeader, members: mergedTeam.members })
          .withLeaderRemovedFromMembers();
      }

      let updatedProposalBuilder = session.context?.viewingAsVendor
        ? proposalBuilder.setVendorTeamTemplateIds(updatedSelectedTemplateIds)
        : proposalBuilder.setClientTeamTemplateIds(updatedSelectedTemplateIds);

      if (updatedTeam) updatedProposalBuilder = updatedProposalBuilder.setVendorTeam(updatedTeam);

      onProposalBuilderUpdated(updatedProposalBuilder);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to apply template. Please try again", {
        variant: "error",
      });
    }
  }

  // Members are merged and de-duplicated by user, so a user is the unit a template can be said to
  // have contributed.
  function getTeamMemberKey(member: EntityMember): string {
    return member.userId?.value ?? "";
  }

  // Names the person a leader/member collision is about. The individuals pool covers everyone the
  // templates and the proposal reference, which is everyone who can collide; anyone it has not
  // loaded yet falls back to the message's own wording.
  function getIndividualFullName(member?: EntityMember | null): string | undefined {
    if (!member) return undefined;
    return individuals.find((individual) => individual.userId.isEqualTo(member.userId))?.fullName;
  }

  // Supplements the recorded contributions for templates that were already checked when the tab
  // mounted, which have no record of their own. `teamTemplates` is one ownership-filtered page, so
  // this can only ever KEEP a member a still-selected template supplies, never take one away: what
  // it cannot see is covered by the contribution records instead.
  function getTeamMemberKeysOfLoadedTemplates(templateIds: Guid[]): string[] {
    return (teamTemplates?.data ?? [])
      .filter((loadedTemplate) => templateIds.some((id) => id.isEqualTo(loadedTemplate.id)))
      .flatMap((loadedTemplate) => loadedTemplate.members.map(getTeamMemberKey));
  }

  function withoutLeaderSupplier(
    suppliers: Readonly<Record<string, string>>,
    templateId: Guid,
  ): Readonly<Record<string, string>> {
    if (!(templateId.value in suppliers)) return suppliers;
    const remaining = { ...suppliers };
    delete remaining[templateId.value];
    return remaining;
  }

  function getNewMembersFromTemplate(template: TeamTemplate): EntityMember[] {
    return _.uniqWith(
      [...template.members, ...selectedMembers],
      (a, b) => (!a.userId && !b.userId) || (a.userId?.isEqualTo(b.userId) ?? false),
    );
  }

  async function getNewLeaderFromTemplate(template: TeamTemplate): Promise<EntityMember | undefined> {
    // A template a reviewer selects supplies its ROSTER only. Its leader is a team-identity change
    // this actor may not make, and the save path drops it, so taking it here would show a leader on
    // screen that the next read of the proposal contradicts (#4854).
    if (!canChangeTeamIdentity) return selectedLeader ?? undefined;
    if (!selectedLeader) return template.leader;

    const templateLeaderAlreadySelected = selectedLeader.userId?.isEqualTo(template.leader.userId);
    if (templateLeaderAlreadySelected) {
      return template.leader;
    } else {
      const response = await confirm({
        title: "Replace leader?",
        message: "Do you want to replace the current vendor-team leader with the one from the template?",
        okButtonText: "Replace",
        cancelButtonText: "Keep Current Leader",
      });

      if (response !== ConfirmResponse.Ok) {
        return selectedLeader;
      }
    }
    return template.leader;
  }

  /**
   * Seating a leader who is currently a team member takes them off the member list, because the two
   * roles are exclusive (#4491). That used to be a silent `return`, so picking a leader who happened
   * to already be a member looked like a broken control. Now the promotion is offered and, once
   * accepted, carried out.
   */
  async function handleTeamLeaderUpdated(newLeader: EntityMember | null): Promise<void> {
    if (!newLeader) {
      updateTeamOnProposalBuilder(undefined, selectedMembers);
      return;
    }

    if (selectedLeader?.isSamePersonAs(newLeader)) return;

    if (!newLeader.legalEntityId) {
      enqueueSnackbar("Selected leader is not associated with an entity", {
        variant: "error",
      });
      return;
    }

    const isCurrentlyMember = selectedMembers.some((member) => newLeader.isSamePersonAs(member));
    if (isCurrentlyMember) {
      const response = await confirm(getPromoteTeamMemberToLeaderPrompt(getIndividualFullName(newLeader)));
      if (response !== ConfirmResponse.Ok) return;
      enqueueSnackbar(getTeamMemberBecameLeaderMessage(getIndividualFullName(newLeader)), { variant: "info" });
    }

    // Seating them takes them off the member list; `updateTeamOnProposalBuilder` is what does it.
    updateTeamOnProposalBuilder(newLeader, selectedMembers);
  }

  // The template's leader checkbox reads "is this person leading the team", so it has to toggle
  // against the leader slot alone. Comparing against the whole team instead made the box clear a
  // perfectly good leader whenever the template's leader happened to be a plain member.
  async function handleTemplateLeaderToggled(member: EntityMember): Promise<void> {
    if (selectedLeader?.isSamePersonAs(member)) {
      updateTeamOnProposalBuilder(undefined, selectedMembers);
      return;
    }

    await handleTeamLeaderUpdated(member);
  }

  function handleTeamMemberAdded(member: EntityMember) {
    if (selectedLeader?.isSamePersonAs(member)) {
      enqueueSnackbar(getTeamLeaderCannotAlsoBeMemberMessage(getIndividualFullName(member)), { variant: "info" });
      return;
    }

    if (getIsMemberAlreadySelected(member)) {
      handleTeamMemberRemoved(member.userId);
      return;
    }
    let updatedMembers = [...selectedMembers, member];
    updateTeamOnProposalBuilder(selectedLeader ?? undefined, updatedMembers);
  }

  /**
   * Adds several members in one builder update.
   *
   * `handleTeamMemberAdded` reads `selectedMembers` from the render it was built in, so calling it
   * once per person in a loop would discard everyone but the last. The leader is filtered rather
   * than warned about: "add all colleagues" is not a claim about any one person, and a leader who
   * is also listed as a member is what `VendorTeam.ValidateInputAsync` refuses.
   */
  function handleTeamMembersAdded(members: EntityMember[]) {
    const additions = members.filter(
      (member) => !selectedLeader?.isSamePersonAs(member) && !getIsMemberAlreadySelected(member),
    );
    if (additions.length === 0) return;

    updateTeamOnProposalBuilder(selectedLeader ?? undefined, [...selectedMembers, ...additions]);
  }

  function getIsMemberAlreadySelected(member: EntityMember | null): boolean {
    if (!member) return false;
    return (
      (selectedLeader?.userId?.isEqualTo(member.userId) ?? false) ||
      selectedMembers.some((m) => m.userId?.isEqualTo(member.userId))
    );
  }

  // A revert restores a member's prior value through the SAME `updateTeamOnProposalBuilder` builder
  // path any other member edit uses, so it flows to `queueProposalUpdate` as a
  // `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw `FieldSet`.
  function handleTeamMemberReverted(revertedMember: EntityMember) {
    const memberExists = selectedMembers.some((member) => member.userId?.isEqualTo(revertedMember.userId));
    const updatedMembers = memberExists
      ? selectedMembers.map((member) =>
          member.userId?.isEqualTo(revertedMember.userId) ? revertedMember : member,
        )
      : [...selectedMembers, revertedMember];
    updateTeamOnProposalBuilder(selectedLeader ?? undefined, updatedMembers);
  }

  // A revert restores the leader's prior value through the SAME `updateTeamOnProposalBuilder` builder
  // path `handleTeamLeaderUpdated` uses, so it flows to `queueProposalUpdate` as a plain
  // `/vendorTeam/leader` `FieldSet` — never a raw whole-team overwrite that would also touch `members`
  // (see proposal-command-builder.ts's scalar `editableDraftSetupFieldPaths`).
  function handleTeamLeaderReverted(revertedLeader: EntityMember) {
    updateTeamOnProposalBuilder(revertedLeader, selectedMembers);
  }

  function handleTeamMemberRemoved(memberUserId?: Guid) {
    if (!memberUserId) {
      // Remove all members
      updateTeamOnProposalBuilder(selectedLeader ?? undefined, []);
      return;
    }

    const updatedMembers = selectedMembers.filter((m) => !m.userId?.isEqualTo(memberUserId));
    updateTeamOnProposalBuilder(selectedLeader ?? undefined, updatedMembers);
  }

  async function handleMarketplaceTeamSelected(team: MarketplaceTeam) {
    if (selectedLeader || selectedMembers.length > 0) {
      const response = await confirm({
        title: "Replace Team?",
        message: "Are you sure you want to replace the current vendor-team?",
        okButtonText: "Replace",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    if (!team.leader.profile?.firstName || !team.leader.profile?.lastName) {
      throw new Error("Team leader profile is undefined");
    }

    const selectedVendorTeam = VendorTeam.FromMarketplaceTeam(team);
    if (!selectedVendorTeam?.marketplaceId) {
      throw new Error("Selected marketplace team is missing a marketplace id");
    }

    onProposalBuilderUpdated(proposalBuilder.setVendorTeam(selectedVendorTeam));
  }

  function handleCreateTemplateClicked() {
    if (!canCreateTemplate) {
      enqueueSnackbar("Select a team leader before creating a template.", { variant: "warning" });
      return;
    }

    setCreateTemplateName(getDefaultTeamTemplateName(proposalBuilder.currentSpec.workAgreement?.name?.value));
    setIsCreateTemplateDialogOpen(true);
  }

  function handleCreateTemplateDialogClose() {
    if (isCreatingTemplate) return;
    setIsCreateTemplateDialogOpen(false);
  }

  async function handleCreateTemplateConfirmed() {
    const templateLeader = selectedLeader;
    const templateName = createTemplateName.trim();
    if (!templateName || !templateLeader?.userId || !templateLeader.legalEntityId || !session.user?.legalEntityId) {
      return;
    }

    try {
      setIsCreatingTemplate(true);
      const response = await createTeamTemplate({
        entityId: session.user.legalEntityId.value,
        team: new VendorTeam({
          leader: templateLeader,
          members: selectedMembers,
          marketplaceId: selectedVendorTeam?.marketplaceId,
        }),
        teamName: new VendorTeamName(templateName),
        creatorType: session.accountType,
      }).unwrap();
      const createdTemplate = TeamTemplate.fromJSON(response);
      if (!createdTemplate.id) throw new Error("Created team template is missing an ID");

      const nextTemplateIds = [...(selectedTemplateIds ?? []), createdTemplate.id];
      const updatedProposalBuilder = session.context?.viewingAsVendor
        ? proposalBuilder.setVendorTeamTemplateIds(nextTemplateIds)
        : proposalBuilder.setClientTeamTemplateIds(nextTemplateIds);

      // The new template arrives already checked, and it was built out of members that were all
      // there beforehand — it added none of them but it supplies every one, which is what keeps them
      // from leaving with some other template the user later deselects.
      setTemplateContributions(
        withTemplateSelected({
          contributions: templateContributions,
          templateId: createdTemplate.id,
          suppliedItemKeys: selectedMembers.map(getTeamMemberKey),
          addedItemKeys: [],
        }),
      );
      onProposalBuilderUpdated(updatedProposalBuilder);
      enqueueSnackbar("Team template created", { variant: "success" });
      setIsCreateTemplateDialogOpen(false);
    } catch (error) {
      console.error("Failed to create team template from proposal", error);
      enqueueSnackbar("Failed to create team template. Please try again.", { variant: "error" });
    } finally {
      setIsCreatingTemplate(false);
    }
  }

  /** The person leading a template, revealed under it — or a loader, while their name is on its way. */
  function buildTemplateLeaderRows(template: TeamTemplate, templateKey: string): TemplateSelectorRow[] {
    // Braced rather than a one-line early return on purpose. Written on one line, the swc-to-istanbul
    // source map drops the whole guard — the condition and its return leave no entry in either the
    // statement map or the branch map — so coverage reports the file as fully exercised here whether
    // or not anything ever renders this loader.
    if (individualQuery.isLoading) {
      return [buildPersonLoaderRow(`${templateKey}-leader-loading`, "Loading leader...")];
    }
    const leaderIndividual = individuals.find((i) => i.userId.isEqualTo(template.leader.userId));
    if (!leaderIndividual) return [];
    return [
      {
        id: `${templateKey}-leader`,
        name: leaderIndividual.fullName,
        content: <TemplatePersonCell name={leaderIndividual.fullName} teamRole="Leader" />,
        selected: selectedLeader?.userId?.isEqualTo(template?.leader?.userId) ?? false,
        onSelectedChanged: () => void handleTemplateLeaderToggled(template?.leader),
      },
    ];
  }

  function isMemberSelected(member: EntityMember): boolean {
    return selectedMembers?.some((m) => m.userId?.isEqualTo(member.userId));
  }

  function handleMemberCheckboxClick(member: EntityMember) {
    if (isMemberSelected(member)) {
      handleTeamMemberRemoved(member.userId);
    } else {
      handleTeamMemberAdded(member);
    }
  }

  /** The template's roster, revealed under it — or a loader, while their names are on their way. */
  function buildTemplateMemberRows(template: TeamTemplate, templateKey: string): TemplateSelectorRow[] {
    // Braced for the same reason as the leader loader above: on one line this guard is invisible to
    // coverage rather than merely uncovered.
    if (individualQuery.isLoading) {
      return [buildPersonLoaderRow(`${templateKey}-members-loading`, "Loading members...")];
    }
    return (template?.members ?? []).map((member, index) => {
      // Someone leading the team is on it, just not as a member — so the box reads as ticked, and it
      // is locked rather than left inert, because the exclusivity rule is not the user's to unpick.
      const isTeamLeader = Boolean(selectedLeader?.isSamePersonAs(member));
      // A person whose individual has not arrived is still named as a person: their user id is not a
      // name, and a row's name is what the panel sorts and searches on (#4830).
      const memberName =
        individuals.find((i) => i.userId.isEqualTo(member.userId))?.fullName ?? "Unnamed member";
      return {
        id: `${templateKey}-member-${member.userId?.value ?? index}`,
        name: memberName,
        content: <TemplatePersonCell name={memberName} teamRole="Member" />,
        selected: isTeamLeader || isMemberSelected(member),
        disabled: isTeamLeader,
        disabledReason: isTeamLeader ? "Already the team leader" : undefined,
        onSelectedChanged: () => handleMemberCheckboxClick(member),
      };
    });
  }

  /** One template's row, with the people it would put on the team folded in under its expander. */
  function buildTemplateRow(template: TeamTemplate): TemplateSelectorRow {
    const isSelected = selectedTemplateIds?.some((id) => id.isEqualTo(template.id)) ?? false;
    const templateKey = template.id?.value ?? template.name.value;
    return {
      id: templateKey,
      name: template.name.value,
      selected: isSelected,
      indeterminate:
        !isSelected &&
        (template.userIsReferenced(selectedLeader?.userId) ||
          selectedMembers.some((member) => template.userIsReferenced(member.userId))),
      children: [
        ...buildTemplateLeaderRows(template, templateKey),
        ...buildTemplateMemberRows(template, templateKey),
      ],
      onSelectedChanged: () => void handleTemplateSelectionToggled(template),
    };
  }

  function renderCreateTemplateAction() {
    if (disableEditing) return undefined;
    return (
      <Tooltip
        title={
          canCreateTemplate
            ? "Create a team template from this proposal team"
            : "Select a team leader before creating a template"
        }
      >
        <span>
          <CreateTemplateAction
            size="small"
            startIcon={<SaveIcon />}
            variant="outlined"
            loading={isCreatingTemplate}
            disabled={!canCreateTemplate || isCreatingTemplate}
            onClick={handleCreateTemplateClicked}
          >
            Create Template
          </CreateTemplateAction>
        </span>
      </Tooltip>
    );
  }

  /**
   * The templates panel, in the shape the Conflicts tab has had all along: an ownership toggle, a
   * search box, and a table of named rows with checkboxes and an expander (#4955).
   *
   * No `tabs` are passed, and none should be added. A team-template panel has ONE source to draw
   * from — the team templates this actor may reach — and a strip with a single tab in it says
   * nothing. The Conflicts panel's second tab lists documents already used on proposals; the domain
   * has no second list of team templates, so a tab here would have to be invented along with a query
   * to fill it.
   *
   * No `sorting` either: the panel holds every row it is showing, so the table sorts them itself.
   */
  function renderTemplatePanel() {
    return (
      <TemplateContainer aria-label="Team templates" role="group">
        <TemplateSelector
          action={renderCreateTemplateAction()}
          collapsedLabel="Team Templates"
          compact={isMdDownDisplay}
          ownership={{
            label: "team templates",
            value: templateOwnershipScope,
            onChange: setTemplateOwnershipScope,
          }}
          pagination={{
            onChange: setTemplatePagination,
            rowCount: teamTemplates?.totalElements ?? 0,
            value: templatePagination,
          }}
          rows={visibleTeamTemplates.map(buildTemplateRow)}
          search={{ onChange: setTemplateSearch, value: templateSearch }}
          selectedCount={selectedTemplateCount}
          status={{
            errorMessage: getErrorMessageFromUnknown(teamTemplatesApiQuery.error),
            isError: teamTemplatesApiQuery.isError,
            isFetching: isLoadingTeamTemplates,
            isLoading: teamTemplatesApiQuery.isLoading,
            onRetry: () => void teamTemplatesApiQuery.refetch(),
          }}
          subject="team templates"
        />
      </TemplateContainer>
    );
  }

  function renderTopControls() {
    const teamRestricted = proposalBuilder.currentSpec.workAgreement?.teamRestricted;
    if (Masked.isMasked(teamRestricted)) return null;

    return (
      <TopControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer 
            active={issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false}
          >
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <PendingChangeControlFrame
          pendingChange={fieldHasPendingChange(pendingChangeFields, ProposalField.TeamRestriction)}
        >
          <ListItem disableGutters sx={{ py: 0 }}>
            {showLockIcon && (
              <ListItemAvatar sx={{ alignItems: "center", display: "inline-flex" }}>
                {lockIcon}
              </ListItemAvatar>
            )}
            <ListItemText
              disableTypography
              primary={
                <FormControlLabel
                  checked={teamRestricted?.value}
                  control={
                    <Switch
                      disabled={disableEditing}
                      onChange={(_event, checked) => {
                        onProposalBuilderUpdated(proposalBuilder.setTeamRestricted(new AHBoolean(checked)));
                      }}
                      color="primary"
                    />
                  }
                  label={teamRestricted?.value ? <strong>Team Restricted</strong> : "Team Not Restricted"}
                />
              }
            />
          </ListItem>
        </PendingChangeControlFrame>
      </TopControls>
    );
  }

  if (
    Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.vendorTeam) ||
    Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.teamRestricted)
  ) {
    return maskedProposalFieldNotice(MaskedProposalField.Team);
  }

  // What "add all colleagues" costs on this draft: a restricted team needs every member's approval
  // before the proposal can be hired. Read after the mask check above, which is what narrows it.
  const draftTeamIsRestricted = proposalBuilder.currentSpec.workAgreement?.teamRestricted?.value ?? false;

  return (
    <>
      <ListContainer container>
        {!disableEditing && (
          <>
            {/*
              One panel, drawn once. The tab used to render the templates twice — a desktop copy and
              a local accordion for the narrow pane, with a CSS media query picking between them — so
              every row, checkbox and Create Template button existed in duplicate. The shared panel
              collapses itself when told the pane is compact, so the second copy is gone and the
              resizable column is all that is left to choose.
            */}
            {isMdDownDisplay ? (
              renderTemplatePanel()
            ) : (
              <ResizableTemplatePanel minimized={isDrawerOpen} selectedCount={selectedTemplateCount}>
                {renderTemplatePanel()}
              </ResizableTemplatePanel>
            )}
            {!isMdDownDisplay && (
              <Grid>
                <Divider orientation="vertical" />
              </Grid>
            )}
          </>
        )}
        <TeamSelectContainer size="grow">
          <TeamSelect
            leader={selectedLeader}
            members={selectedMembers}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            disableEditing={disableEditing}
            canChangeTeamIdentity={canChangeTeamIdentity}
            fieldLockTooltip={fieldLockTooltip}
            pendingChangeFields={pendingChangeFields}
            topControls={renderTopControls()}
            onLeaderUpdated={
              canChangeTeamIdentity ? (newLeader) => void handleTeamLeaderUpdated(newLeader) : undefined
            }
            onMemberAdded={handleTeamMemberAdded}
            onMembersAdded={handleTeamMembersAdded}
            teamRestricted={draftTeamIsRestricted}
            onMemberRemoved={handleTeamMemberRemoved}
            onMemberReverted={handleTeamMemberReverted}
            onLeaderReverted={canChangeTeamIdentity ? handleTeamLeaderReverted : undefined}
            onMarketplaceTeamSelected={canChangeTeamIdentity ? handleMarketplaceTeamSelected : undefined}
            selectionPresentation="drawer"
            onSelectionDrawerOpenChange={onSelectionDrawerOpenChange}
          />
        </TeamSelectContainer>
      </ListContainer>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isCreateTemplateDialogOpen}
        onClose={handleCreateTemplateDialogClose}
      >
        <DialogTitle>Create Team Template</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            margin="dense"
            value={createTemplateName}
            onChange={(event) => setCreateTemplateName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void handleCreateTemplateConfirmed();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={isCreatingTemplate} onClick={handleCreateTemplateDialogClose}>
            Cancel
          </Button>
          <LoadingButton
            loading={isCreatingTemplate}
            disabled={!createTemplateName.trim()}
            onClick={handleCreateTemplateConfirmed}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * A person under a team template: what they are to it, then what they are called.
 *
 * The list this panel replaced grouped them under "Leader" and "Members" subheaders. A table of
 * named rows has nowhere to put a subheader, so the role travels with the person instead.
 */
function TemplatePersonCell(props: Readonly<{ name: string; teamRole: string }>) {
  return (
    <PersonNameCell>
      <PersonRole>{props.teamRole}</PersonRole>
      <PersonName>{props.name}</PersonName>
    </PersonNameCell>
  );
}

/**
 * A row standing in for people whose names have not arrived yet.
 *
 * It is disabled, which is what keeps it inert: the panel neither selects nor clicks through a
 * disabled row, so `onSelectedChanged` is never reached.
 */
function buildPersonLoaderRow(id: string, text: string): TemplateSelectorRow {
  return {
    id,
    name: text,
    content: <Loader size={24} thickness={4} text={text} />,
    selected: false,
    disabled: true,
    onSelectedChanged: () => undefined,
  };
}

export function getDefaultTeamTemplateName(proposalName?: string | null): string {
  const trimmedName = proposalName?.trim();
  return trimmedName ? `${trimmedName} Team` : "Proposal Team";
}
